/** @jsxImportSource @emotion/react */
import React, {
  useState,
  useCallback,
  useContext,
  useMemo,
  memo,
  useRef,
  useEffect
} from 'react'
import isArray from 'lodash/isArray'
import MenuServiceContext from '@signal/core/menuService/Context'
import {
  MenuServiceFetchContextType,
  MenuServiceItemType,
  MenuServiceItemsType,
  MenuServiceSubItemType
} from '@signal/core/menuService/types'
import { useMenuLinks } from '@signal/core/menuService/useMenuLink'
import withCommonSnapshotsOnly from '@signal/hoc/withCommonSnapshotsOnly'
import {
  getMenuExpandedMode,
  getMenuPanelWidth,
  toggleMenuExpandedMode
} from '@signal/layouts/utils'
import TenantMenu from '../TenantMenu'
import {
  OuterWrapper,
  InnerWrapper,
  LogoWrapper,
  MenuWrapper,
  ToggleButton,
  BottomSection,
  ScrollSection,
  ScrollSectionProps,
  ScrollOverlayTop,
  ScrollOverlayBottom,
  motionStyled
} from './styles'
import Typography from '../Typography'
import MenuItem from './MenuItem'
import { AnimatePresence } from 'framer-motion'
import { useScrollTracking } from './utils'
import ScrollButtons from './ScrollButtons'
import HoverPopup from './HoverPopup'
import PureInput from '../PureInput'
import { Search, Close } from '@material-ui/icons'
import isTest from '@purecarslabs/signal-utils/env/isTest'

const year = isTest() ? 2025 : new Date().getFullYear()

const MotionScrollSection = motionStyled<ScrollSectionProps>(ScrollSection)

type WrapperComponentType = React.ComponentType<
  React.PropsWithChildren<
    React.HTMLAttributes<HTMLDivElement> & {
      $isExpanded: boolean
      $menuPanelWidth?: string
    }
  >
>

const OuterWrapperWithChildren = OuterWrapper as WrapperComponentType

const InnerWrapperWithChildren = InnerWrapper as WrapperComponentType

const LogoWrapperWithChildren = LogoWrapper as WrapperComponentType

const MenuWrapperWithChildren = MenuWrapper as WrapperComponentType

const ToggleButtonWithChildren = ToggleButton as React.ComponentType<
  React.ButtonHTMLAttributes<HTMLButtonElement>
>

type OnNavigateType = (link: string) => void

export interface LeftMenuNewProps {
  appSupportedQP?: Array<string>
  extraMenuItems?: Array<MenuServiceItemType>
  location?: Location
  onNavigate?: OnNavigateType
}

export function LeftMenuNew({
  appSupportedQP = [],
  extraMenuItems = [],
  location,
  onNavigate
}: LeftMenuNewProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(() => getMenuExpandedMode()) // Load from localStorage
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const { response: serviceMenu = [] } =
    useContext<MenuServiceFetchContextType>(MenuServiceContext)

  const { navMenu, allSubMenuItems } = useMemo(() => {
    // Handle legitimate API failures gracefully (empty/invalid responses)
    const validServiceMenu = isArray(serviceMenu) ? serviceMenu : []

    // Development warning for component misuse (not API failure)
    if (process.env.NODE_ENV === 'development' && validServiceMenu.length > 0) {
      const firstIcon = validServiceMenu[0]?.icon
      if (firstIcon && typeof firstIcon === 'string') {
        console.warn(
          'LeftMenuNew: Received string icons instead of React components. ' +
            'This component should be used within MenuServiceLoader for proper icon processing.'
        )
      }
    }

    // Transform menu data to ensure consistent structure (children → subMenuItems)
    type MenuItemShape = { children?: unknown[]; [k: string]: unknown }
    const transformMenuData = (items: unknown[]): MenuServiceItemsType => {
      return items.map((item: unknown) => {
        const { children, ...rest } = item as MenuItemShape
        return {
          ...rest,
          subMenuItems: children ? transformMenuData(children) : []
        } as MenuServiceItemType
      }) as MenuServiceItemsType
    }

    const transformedServiceMenu = transformMenuData(validServiceMenu)
    const navMenu: MenuServiceItemsType =
      transformedServiceMenu.concat(extraMenuItems)

    // Recursively collect ALL subMenuItems from ALL levels for query parameter processing
    const collectAllSubMenuItems = (
      items: MenuServiceItemsType
    ): Array<MenuServiceSubItemType> => {
      const result: Array<MenuServiceSubItemType> = []
      items.forEach(item => {
        if (item.subMenuItems) {
          // Add direct sub-items
          result.push(...item.subMenuItems)
          // Recursively collect sub-sub-items
          result.push(...collectAllSubMenuItems(item.subMenuItems))
        }
      })
      return result
    }

    const allSubMenuItems: Array<MenuServiceSubItemType> =
      collectAllSubMenuItems(navMenu)

    return {
      navMenu,
      allSubMenuItems
    }
  }, [extraMenuItems, serviceMenu])

  // Debounce search query with 0.3 second delay
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [searchQuery])

  // Filter menu items based on debounced search query
  const filteredNavMenu = useMemo(() => {
    if (!debouncedSearchQuery.trim()) {
      return navMenu
    }

    const query = debouncedSearchQuery.toLowerCase().trim()

    const filterMenuItems = (
      items: MenuServiceItemsType
    ): MenuServiceItemsType => {
      return items.reduce<MenuServiceItemsType>((acc, item) => {
        const itemNameMatches = item.name.toLowerCase().includes(query)

        // Recursively filter submenu items
        const filterSubMenuItems = (
          subItems: Array<MenuServiceSubItemType>
        ): Array<MenuServiceSubItemType> => {
          return subItems.reduce<Array<MenuServiceSubItemType>>(
            (subAcc, subItem) => {
              const subItemNameMatches = subItem.name
                .toLowerCase()
                .includes(query)

              // Filter third-level items
              const matchingThirdLevelItems = (
                subItem.subMenuItems || []
              ).filter((thirdLevelItem: MenuServiceSubItemType) =>
                thirdLevelItem.name.toLowerCase().includes(query)
              )

              // Include sub-item if its name matches OR if it has matching third-level items
              if (subItemNameMatches || matchingThirdLevelItems.length > 0) {
                subAcc.push({
                  ...subItem,
                  // If sub-item doesn't match but third-level items do,
                  // only show the matching third-level items
                  subMenuItems: subItemNameMatches
                    ? subItem.subMenuItems
                    : matchingThirdLevelItems
                })
              }

              return subAcc
            },
            []
          )
        }

        const filteredSubMenuItems = filterSubMenuItems(item.subMenuItems || [])

        // Include item if its name matches OR if it has matching submenu items (at any level)
        if (itemNameMatches || filteredSubMenuItems.length > 0) {
          acc.push({
            ...item,
            // If main menu item doesn't match but submenu items do,
            // only show the matching submenu items
            subMenuItems: itemNameMatches
              ? item.subMenuItems
              : filteredSubMenuItems
          })
        }

        return acc
      }, [])
    }

    return filterMenuItems(navMenu)
  }, [navMenu, debouncedSearchQuery])

  // Track which menus should be auto-expanded due to search results
  const searchExpandedMenus = useMemo(() => {
    if (!debouncedSearchQuery.trim()) {
      return new Set<string>()
    }

    const query = debouncedSearchQuery.toLowerCase().trim()
    const expanded = new Set<string>()

    filteredNavMenu.forEach(item => {
      const hasMatchingSubItems = (item.subMenuItems || []).some(
        (subItem: MenuServiceSubItemType) => {
          const subItemMatches = subItem.name.toLowerCase().includes(query)
          const hasMatchingThirdLevelItems = (subItem.subMenuItems || []).some(
            (thirdLevelItem: MenuServiceSubItemType) =>
              thirdLevelItem.name.toLowerCase().includes(query)
          )
          return subItemMatches || hasMatchingThirdLevelItems
        }
      )

      // If this menu has matching sub-items (at any level), it should be expanded
      if (hasMatchingSubItems) {
        expanded.add(item.name)
      }
    })

    return expanded
  }, [filteredNavMenu, debouncedSearchQuery])

  // Track which sub-items should be auto-expanded due to search results in their third-level items
  const searchExpandedSubItems = useMemo(() => {
    if (!debouncedSearchQuery.trim()) {
      return new Set<string>()
    }

    const query = debouncedSearchQuery.toLowerCase().trim()
    const expandedSubItems = new Set<string>()

    filteredNavMenu.forEach(item => {
      ;(item.subMenuItems || []).forEach(subItem => {
        const hasMatchingThirdLevelItems = (subItem.subMenuItems || []).some(
          (thirdLevelItem: MenuServiceSubItemType) =>
            thirdLevelItem.name.toLowerCase().includes(query)
        )

        // If this sub-item has matching third-level items, it should be expanded
        if (hasMatchingThirdLevelItems) {
          expandedSubItems.add(subItem.link || subItem.name)
        }
      })
    })

    return expandedSubItems
  }, [filteredNavMenu, debouncedSearchQuery])

  // Process links through useMenuLinks to get query parameter persistence
  // This ensures that user context (filters, date ranges, etc.) is maintained across navigation
  const [processedSubMenuLinks, processedNavMenuLinks] = useMenuLinks(
    allSubMenuItems,
    navMenu,
    appSupportedQP
  )

  // Create a mapping from original links to processed links for easy lookup
  // This allows us to maintain the accordion structure while using processed links
  const linkMapping = useMemo(() => {
    const mapping = new Map<string, string>()

    // Map submenu links - these get query parameters applied
    allSubMenuItems.forEach((item, index) => {
      if (processedSubMenuLinks[index]) {
        mapping.set(item.link, processedSubMenuLinks[index])
      }
    })

    // Map nav menu links - for any root items that have direct links
    navMenu.forEach((item, index) => {
      if (item.link && processedNavMenuLinks[index]) {
        mapping.set(item.link, processedNavMenuLinks[index])
      }
    })

    return mapping
  }, [allSubMenuItems, processedSubMenuLinks, navMenu, processedNavMenuLinks])

  // Helper function to get processed link with query parameters or fallback to original.
  const getProcessedLink = useCallback(
    (originalLink: string): string =>
      linkMapping.get(originalLink) || originalLink,
    [linkMapping]
  )

  // State for tracking which menu is open (needed before scroll tracking)
  const [openMenu, setOpenMenu] = useState<string | null>(null)

  // State for tracking which menu should show popup in collapsed mode
  const [popupMenu, setPopupMenu] = useState<string | null>(null)
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 })
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const showDebounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleToggle = useCallback(() => {
    const newValue = toggleMenuExpandedMode()
    setIsExpanded(newValue)
  }, [])

  const handleClearSearch = useCallback(() => {
    setSearchQuery('')
  }, [])

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value)
    },
    []
  )

  // Clear any existing hide timeout
  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }, [])

  // Clear any existing show debounce timeout
  const clearShowDebounceTimeout = useCallback(() => {
    if (showDebounceTimeoutRef.current) {
      clearTimeout(showDebounceTimeoutRef.current)
      showDebounceTimeoutRef.current = null
    }
  }, [])

  // Handle popup menu state with debouncing
  const handleMenuPopupShow = useCallback(
    (menuName: string, position: { top: number; left: number }) => {
      clearHideTimeout() // Cancel any pending hide
      clearShowDebounceTimeout() // Cancel any pending show

      // If popup is already showing the same menu, update position immediately
      if (popupMenu === menuName) {
        setPopupPosition(position)
        return
      }

      // If it's a different menu or no popup showing, debounce the change
      showDebounceTimeoutRef.current = setTimeout(() => {
        setPopupMenu(menuName)
        setPopupPosition(position)
      }, 175) // 175ms debounce - if this is too low, the animation will be very erratic and show huge height on the popup as it briefly shows height for two menus contents
    },
    [clearHideTimeout, clearShowDebounceTimeout, popupMenu]
  )

  const handleMenuPopupHide = useCallback(() => {
    // Cancel any pending show and add delay before hiding to allow smooth layout transitions
    clearShowDebounceTimeout()
    clearHideTimeout()
    hideTimeoutRef.current = setTimeout(() => {
      setPopupMenu(null)
    }, 300) // 300ms delay for smooth layoutId transitions
  }, [clearHideTimeout, clearShowDebounceTimeout])

  // Hide popup when sidebar expands
  useEffect(() => {
    if (isExpanded) {
      clearHideTimeout()
      clearShowDebounceTimeout()
      setPopupMenu(null)
    }
  }, [isExpanded, clearHideTimeout, clearShowDebounceTimeout])

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      clearHideTimeout()
      clearShowDebounceTimeout()
    }
  }, [clearHideTimeout, clearShowDebounceTimeout])

  const handleTopLevelItemClick = useCallback(
    (itemName: string) => () => {
      const hasSearchResults = searchExpandedMenus.has(itemName)
      if (hasSearchResults && debouncedSearchQuery.trim()) {
        setOpenMenu(itemName)
        return
      }

      setOpenMenu(currentOpenMenu =>
        currentOpenMenu === itemName ? null : itemName
      )
    },
    [debouncedSearchQuery, searchExpandedMenus]
  )

  const handleSubItemClick = useCallback(
    (link: string) => {
      if (link !== '#' && onNavigate) {
        const processedLink = getProcessedLink(link)
        onNavigate(processedLink)
      }
    },
    [getProcessedLink, onNavigate]
  )

  const handlePopupShow = useCallback(
    (menuName: string) => (position: { top: number; left: number }) => {
      handleMenuPopupShow(menuName, position)
    },
    [handleMenuPopupShow]
  )

  // Use scroll tracking hook - depend on openMenu state, expansion state, menu count, and debounced search query for content changes
  const { scrollRef, scrollState, scrollToTop, scrollToBottom } =
    useScrollTracking([
      openMenu,
      isExpanded,
      filteredNavMenu.length,
      debouncedSearchQuery
    ])

  // If no location or onNavigate, render simple menu
  if (!location || !onNavigate) {
    return (
      <OuterWrapperWithChildren
        $isExpanded={isExpanded}
        $menuPanelWidth={getMenuPanelWidth()}
      >
        <InnerWrapperWithChildren
          $isExpanded={isExpanded}
          $menuPanelWidth={getMenuPanelWidth()}
        >
          <LogoWrapperWithChildren $isExpanded={isExpanded}>
            <TenantMenu compact={!isExpanded} />
          </LogoWrapperWithChildren>

          <MenuWrapperWithChildren $isExpanded={isExpanded} />

          <ToggleButtonWithChildren
            onClick={handleToggle}
            title={isExpanded ? 'Collapse menu' : 'Expand menu'}
            aria-label={isExpanded ? 'Collapse menu' : 'Expand menu'}
          >
            {isExpanded ? '‹' : '›'}
          </ToggleButtonWithChildren>
        </InnerWrapperWithChildren>
      </OuterWrapperWithChildren>
    )
  }

  return (
    <OuterWrapperWithChildren
      $isExpanded={isExpanded}
      $menuPanelWidth={getMenuPanelWidth()}
    >
      <InnerWrapperWithChildren
        $isExpanded={isExpanded}
        $menuPanelWidth={getMenuPanelWidth()}
      >
        <LogoWrapperWithChildren $isExpanded={isExpanded}>
          <TenantMenu compact={!isExpanded} />
        </LogoWrapperWithChildren>

        {/* Search bar - only show when expanded */}
        {isExpanded && (
          <div style={{ padding: '12px 16px 8px 16px' }}>
            <PureInput
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search menu..."
              leftOrnament={
                searchQuery.trim() ? (
                  <Close
                    fontSize="small"
                    onClick={handleClearSearch}
                    style={{ cursor: 'pointer' }}
                  />
                ) : (
                  <Search fontSize="small" />
                )
              }
              $stretch
            />
          </div>
        )}

        <MenuWrapperWithChildren
          $isExpanded={isExpanded}
          style={{ position: 'relative' }}
        >
          {/* Scroll gradient overlays */}
          {scrollState.canScroll && !scrollState.isAtTop && (
            <ScrollOverlayTop />
          )}
          {scrollState.canScroll && !scrollState.isAtBottom && (
            <ScrollOverlayBottom />
          )}

          {/* Scroll buttons */}
          <ScrollButtons
            scrollToTop={scrollToTop}
            scrollToBottom={scrollToBottom}
            canScroll={scrollState.canScroll}
            isAtTop={scrollState.isAtTop}
            isAtBottom={scrollState.isAtBottom}
          />

          <MotionScrollSection layout $isExpanded={isExpanded} ref={scrollRef}>
            {/* Show helpful message when no menu items available */}
            {filteredNavMenu.length === 0 && debouncedSearchQuery.trim() && (
              <div style={{ padding: '16px', textAlign: 'center' }}>
                <Typography color="textSecondary" variant="body5">
                  No items found for "{debouncedSearchQuery}"
                </Typography>
              </div>
            )}
            {navMenu.length === 0 && !debouncedSearchQuery.trim() && (
              <div style={{ padding: '16px', textAlign: 'center' }}>
                <Typography color="textSecondary" variant="body5">
                  No navigation available
                </Typography>
              </div>
            )}

            {/* Render actual menu items from filteredNavMenu */}
            <AnimatePresence mode="popLayout">
              {filteredNavMenu.map((item: MenuServiceItemType) => {
                // Determine if this menu should be open
                // It should be open if: user clicked to open it, OR it has search results
                const shouldBeOpen =
                  openMenu === item.name || searchExpandedMenus.has(item.name)

                return (
                  <MenuItem
                    key={'top-level-menu-item-' + item.name}
                    layoutId={'menu-item-' + item.name}
                    name={item.name}
                    Icon={
                      item.icon as React.ComponentType<Record<string, unknown>>
                    }
                    subMenuItems={item.subMenuItems}
                    currentPathname={location?.pathname}
                    open={shouldBeOpen}
                    onClick={handleTopLevelItemClick(item.name)}
                    onSubItemClick={handleSubItemClick}
                    getProcessedLink={getProcessedLink}
                    isExpanded={isExpanded}
                    onPopupShow={handlePopupShow(item.name)}
                    onPopupHide={handleMenuPopupHide}
                    searchExpandedSubItems={searchExpandedSubItems}
                  />
                )
              })}
            </AnimatePresence>
          </MotionScrollSection>

          <BottomSection>
            <Typography>{`© ${year} PureCars Technologies`}</Typography>
            <Typography
              css={{
                opacity: 0.5
              }}
            >
              All rights reserved.
            </Typography>
          </BottomSection>
        </MenuWrapperWithChildren>

        <ToggleButtonWithChildren
          onClick={handleToggle}
          title={isExpanded ? 'Collapse menu' : 'Expand menu'}
          aria-label={isExpanded ? 'Collapse menu' : 'Expand menu'}
        >
          {isExpanded ? '‹' : '›'}
        </ToggleButtonWithChildren>
      </InnerWrapperWithChildren>

      {/* Global hover popup for collapsed mode - always rendered to allow exit animations */}
      {(() => {
        const currentMenuItem = popupMenu
          ? filteredNavMenu.find(item => item.name === popupMenu)
          : null
        return (
          <HoverPopup
            isVisible={!!popupMenu && !!currentMenuItem}
            name={currentMenuItem?.name || ''}
            subMenuItems={currentMenuItem?.subMenuItems || []}
            onSubItemClick={handleSubItemClick}
            currentPathname={location?.pathname}
            getProcessedLink={getProcessedLink}
            position={popupPosition}
            onMouseEnter={clearHideTimeout} // Keep popup open when hovering over it
            onMouseLeave={handleMenuPopupHide}
            searchExpandedSubItems={searchExpandedSubItems}
          />
        )
      })()}
    </OuterWrapperWithChildren>
  )
}

export default memo(withCommonSnapshotsOnly(LeftMenuNew))
